import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DatabaseZap } from "lucide-react";
import Spinner from "../../../components/shared/Spinner";
import Topbar from "./components/Topbar";
import Content from "./Content";
import EventDrawer from "./components/drawers/Event";
import ListView from "./components/drawers/ListView";
import { useScheduleContext } from "./hooks/context/useScheduleContext";
import { useFilters } from "../../../utils/hooks/useFilters";
import { useScheduleApi } from "./api/useScheduleApi";
import { useActionsModal } from "./hooks/modals/useActionsModal";
import { useCopyModal } from "./hooks/modals/useCopyModal";
import { useDeleteModal } from "./hooks/modals/useDeleteModal";
import { useAssignModal } from "./hooks/modals/useAssignModal";
import { useRequestDrawer } from "./hooks/drawers/useRequestDrawer";
import { useProfileSheet } from "../../../components/shared/ProfileSheet/hooks/useProfileSheet";
import { useTransferModal } from "./hooks/modals/useTransferModal";
import { useImportModal } from "./hooks/modals/useImportModal";
import { useShiftTradeDrawer } from "./hooks/drawers/useShiftTradeDrawer";
import { useClaimShiftDrawer } from "./hooks/drawers/useClaimShiftDrawer";
import { useApproveDialog } from "./hooks/modals/useApproveDialog";
import { useDenyDialog } from "./hooks/modals/useDenyDialog";
import { useSaveDayAsTemplateModal } from "./hooks/modals/useSaveDayAsTemplateModal";
import { useLoadTemplatesModal } from "./hooks/modals/useLoadTemplatesModal";
import { useMoveToOpenShiftsModal } from "./hooks/modals/useMoveToOpenShiftsModal";
import { useCountUnpublishedQuery } from "./api/useCountUnpublishedQuery";
import { useOpenShiftsQuery, useOpenShiftsQueryKey } from "./api/useOpenShiftsQuery";
import { useCtrlShiftSHandler } from "./hooks/useCtrlShiftSHandler";
import { usePermissions } from "./hooks/usePermissions";
import { useHeightCssVariable } from "./hooks/useHeightCssVariable";
import { useIsTemplate } from "./hooks/useIsTemplate";
import { useUser } from "../../../utils/hooks/user";
import "./style.scss";

function Component() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { id } = useParams();
    const user = useUser();
    const [filters] = useFilters();
    const { fetchData } = useScheduleApi();

    const {
        refetch,
        type,
        dispatch,
        viewBy,
        template,
        loadingTemplates,
        templates,
        listView
    } = useScheduleContext();
    const { canCreate } = usePermissions();
    const isTemplate = useIsTemplate();
    const { open: openActionsModal, modal: actionsModal } = useActionsModal();
    const { open: openCopyModal, modal: copyModal } = useCopyModal();
    const { open: openDeleteModal, modal: deleteModal } = useDeleteModal();
    const { open: openAssignModal, modal: assignModal } = useAssignModal();
    const { open: openProfileSheet, sheet: profileSheet } = useProfileSheet();
    const { open: openTransferModal, modal: transferModal } = useTransferModal();
    const { open: openShiftTradeDrawer, drawer: shiftTradeDrawer } = useShiftTradeDrawer();
    const { open: openSaveDayAsTemplate, modal: saveDayAsTemplateModal } = useSaveDayAsTemplateModal();
    const { open: openLoadTemplatesModal, modal: loadTemplatesModal } = useLoadTemplatesModal();
    const { open: openImportModal, modal: importModal } = useImportModal();
    const { open: openMoveToOpenShiftsModal, modal: moveToOpenSHiftsModal } = useMoveToOpenShiftsModal();

    const openShiftsQueryKey = useOpenShiftsQueryKey();
    const openShiftsOptions = useMemo(() => ({ queryKey: openShiftsQueryKey }), [openShiftsQueryKey]);
    // offer-shifts and swap-shifts (Requests drawer / widgets) both live under /request/my-team
    const myTeamRequestsOptions = useMemo(() => ({ queryKey: ["/request/my-team"] }), []);

    const { open: openClaimShiftDrawer, drawer: claimShiftDrawer } = useClaimShiftDrawer({
        queryOptions: openShiftsOptions
    });

    const { open: openApproveModal, dialog: approveModal } = useApproveDialog({ queryOptions: myTeamRequestsOptions });
    const { open: openDenyModal, dialog: denyModal } = useDenyDialog({ queryOptions: myTeamRequestsOptions });
    const { open: openRequestDrawer, drawer: requestDrawer } = useRequestDrawer({
        approve: openApproveModal,
        deny: openDenyModal
    });

    // Notifications link here with ?requests=<tab> to open the Requests drawer on that tab
    // and optionally ?request=<id> to highlight that request in the tab
    const requestsTab = searchParams.get("requests");
    const focusedRequest = searchParams.get("request");
    useEffect(() => {
        if (!requestsTab || type !== "home") {
            return;
        }
        openRequestDrawer({ menu: requestsTab, focusedRequest });
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("requests");
            next.delete("request");
            return next;
        }, { replace: true });
    }, [requestsTab, focusedRequest, type, openRequestDrawer, setSearchParams]);

    useCountUnpublishedQuery();
    useOpenShiftsQuery();
    useCtrlShiftSHandler();

    useLayoutEffect(() => {
        const controller = new AbortController();
        const _params = filters;
        if (["profile", "my-profile"].includes(type)) {
            _params.user = id || user?.id;
        }
        if (filters?.mode === "month" && filters?.viewBy) {
            return;
        }
        if (!templates?.[0]?.id && isTemplate) {
            return;
        }
        fetchData(_params, controller);
        return () => controller.abort();
    }, [isTemplate, filters, refetch, viewBy, type, templates, fetchData, id, user?.id]);

    useEffect(() => {
        dispatch({ type: "weekly-summary", payload: false });
        if (filters?.mode === "month") {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete("viewBy");
                return next;
            }, { replace: true });
            // dispatch({ type: "view-by", payload: "name" });
        }
    }, [dispatch, filters?.mode, setSearchParams]);

    useEffect(() => {
        if (template?.id !== filters?.template?.id) {
            dispatch({ type: "set-template", payload: filters?.template });
        }
    }, [template?.id, filters?.template?.id, dispatch, filters?.template]);

    // Expose the topbar height so the team content and unassigned shifts panels shrink by it
    const topbarRef = useRef<HTMLDivElement>(null);
    useHeightCssVariable(topbarRef, "--topbar-height");

    return (
        <div className="schedule-component tw-p-5">
            {/* flow-root keeps the Topbar's margins inside the wrapper, so they are part of the measured height */}
            <div ref={topbarRef} className="tw-flow-root">
                <Topbar
                    openActionsModal={openActionsModal}
                    openCopyModal={openCopyModal}
                    openDeleteModal={openDeleteModal}
                    openRequestDrawer={openRequestDrawer}
                    openLoadTemplatesModal={openLoadTemplatesModal}
                    openApproveModal={openApproveModal}
                    openDenyModal={openDenyModal}
                    openImportModal={openImportModal}
                />
            </div>
            {(loadingTemplates && isTemplate) ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-7">
                    <Spinner className="tw-size-8 tw-text-primary" />
                </div>
            ) : (templates?.length === 0 && isTemplate) ? (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-center tw-text-muted-foreground tw-border tw-border-dashed tw-rounded-lg">
                    <DatabaseZap className="tw-text-primary tw-mb-2" size={30} />
                    <h3 className="tw-text-lg tw-font-medium tw-text-foreground">
                        {t("no-schedule-template-yet")}
                    </h3>
                    <p className="tw-mt-2 tw-max-w-md">
                        {(canCreate && type !== "my-profile")
                            ? t("create-first-template")
                            : t("no-templates-contact-administrator")}
                    </p>
                </div>
            ) : (
                <Content
                    openActionsModal={openActionsModal}
                    openCopyModal={openCopyModal}
                    openDeleteModal={openDeleteModal}
                    openAssignModal={openAssignModal}
                    openProfileSheet={openProfileSheet}
                    openTransferModal={openTransferModal}
                    openShiftTradeDrawer={openShiftTradeDrawer}
                    openClaimShiftDrawer={openClaimShiftDrawer}
                    openSaveDayAsTemplate={openSaveDayAsTemplate}
                    openLoadTemplatesModal={openLoadTemplatesModal}
                    openMoveToOpenShiftsModal={openMoveToOpenShiftsModal}
                />
            )}
            <EventDrawer openDeleteModal={openDeleteModal} />
            {listView?.type && <ListView />}

            {actionsModal}
            {copyModal}
            {deleteModal}
            {requestDrawer}
            {assignModal}
            {profileSheet}
            {transferModal}
            {shiftTradeDrawer}
            {saveDayAsTemplateModal}
            {claimShiftDrawer}
            {loadTemplatesModal}
            {importModal}
            {approveModal}
            {denyModal}
            {moveToOpenSHiftsModal}
        </div>
    );
}

export default Component;
